import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { PageSpinner } from '@/components/ui/Spinner'
import { Shell } from '@/components/layout/Shell'
import { AdminShell } from '@/components/layout/AdminShell'
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary'
import { useAuthStore } from '@/store/auth'
import { restoreAuthState } from '@/api/client'

// ─── Lazy pages — coaching app ───────────────────────────────────────────────
const Login              = lazy(() => import('@/pages/Login'))
const ForgotPassword     = lazy(() => import('@/pages/ForgotPassword'))
const ResetPassword      = lazy(() => import('@/pages/ResetPassword'))
const ChangePassword     = lazy(() => import('@/pages/ChangePassword'))
const MagicLinkSent      = lazy(() => import('@/pages/MagicLinkSent'))
const MagicLinkVerify    = lazy(() => import('@/pages/MagicLinkVerify'))
const SetupPassword      = lazy(() => import('@/pages/SetupPassword'))
const OAuthConsent       = lazy(() => import('@/pages/OAuthConsent'))
const Dashboard      = lazy(() => import('@/pages/Dashboard'))
const Schedule       = lazy(() => import('@/pages/Schedule'))
const GamePlanner    = lazy(() => import('@/pages/GamePlanner'))
const LineupEditor   = lazy(() => import('@/pages/LineupEditor'))
const Roster         = lazy(() => import('@/pages/Roster'))
const PlayerDetail   = lazy(() => import('@/pages/PlayerDetail'))
const PitchingWorkload = lazy(() => import('@/pages/PitchingWorkload'))
const RulesetAdmin   = lazy(() => import('@/pages/RulesetAdmin'))
const Settings       = lazy(() => import('@/pages/Settings'))

// ─── Lazy pages — super admin ─────────────────────────────────────────────────
const Tenants        = lazy(() => import('@/pages/admin/Tenants'))
const AdminSettings  = lazy(() => import('@/pages/admin/Settings'))
const OAuthClients   = lazy(() => import('@/pages/admin/OAuthClients'))

const NotFound = () => (
  <div className="flex items-center justify-center h-full py-24 text-gray-400 text-sm">
    404 — Page not found
  </div>
)

// After login, redirect to the right home based on role.
function RootRedirect() {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  return <Navigate to={user.is_super_admin ? '/admin/tenants' : '/dashboard'} replace />
}

export default function App() {
  useEffect(() => {
    restoreAuthState()
  }, [])

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/magic-link-sent" element={<MagicLinkSent />} />
        <Route path="/magic-link/verify" element={<MagicLinkVerify />} />
        <Route path="/oauth/consent" element={<OAuthConsent />} />

        {/* ── Super admin app ──────────────────────────────────────────── */}
        <Route element={<AdminShell />}>
          <Route path="/admin/tenants" element={<Tenants />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/oauth-clients" element={<OAuthClients />} />
        </Route>

        {/* ── Coaching app ─────────────────────────────────────────────── */}
        <Route element={<Shell />}>
          <Route path="/dashboard"                      element={<Dashboard />} />
          <Route path="/schedule"                       element={<Schedule />} />
          <Route path="/schedule/:gameId"               element={<GamePlanner />} />
          <Route path="/schedule/:gameId/lineup"        element={<LineupEditor />} />
          <Route path="/roster"                         element={<Roster />} />
          <Route path="/roster/:playerId"               element={<PlayerDetail />} />
          <Route path="/pitching"                       element={<PitchingWorkload />} />
          <Route path="/rules"                          element={<RulesetAdmin />} />
          <Route path="/settings"                       element={<Settings />} />
          <Route path="/change-password"                element={<ChangePassword />} />
          <Route path="/setup-password"                 element={<SetupPassword />} />
          <Route path="*"                               element={<NotFound />} />
        </Route>

        {/* ── Root redirect ─────────────────────────────────────────────── */}
        <Route index element={<RootRedirect />} />
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  )
}

